import React, { useState, useEffect } from 'react';
import { X, Star } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ImageUploader } from './ImageUploader';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SIZE_OPTIONS = {
  Adulto: ['S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'],
  Franelillas: ['S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'],
  Dama: ['S', 'M', 'L', 'XL'],
  'Niños': ['4', '6', '8', '10', '12', '14', '16'],
  Gorras: ['UNICA', 'S/M', 'L/XL'],
  Chaquetas: ['S', 'M', 'L', 'XL', '2XL'],
  Suéter: ['S', 'M', 'L', 'XL', '2XL'],
  'Balón': ['5', '4', 'Futsal'],
  Nba: ['S', 'M', 'L', 'XL', '2XL'],
  Mlb: ['S', 'M', 'L', 'XL', '2XL'],
  Nfl: ['S', 'M', 'L', 'XL', '2XL'],
  Lvbp: ['S', 'M', 'L', 'XL', '2XL'],
  Shorts: ['S', 'M', 'L', 'XL', '2XL'],
  Uniforme: ['S', 'M', 'L', 'XL', '2XL', '4', '6', '8', '10', '12', '14', '16'],
  F1: ['S', 'M', 'L', 'XL', '2XL'],
  'Pantalones Urbanas': ['28', '30', '32', '34', '36', '38'],
  'Bermudas Urbanas': ['28', '30', '32', '34', '36', '38'],
  'Camisetas Urbanas': ['S', 'M', 'L', 'XL', '2XL', '3XL'],
};

export const EditProductModal = ({ isOpen, onClose, product, onProductUpdated }) => {
  const [formData, setFormData] = useState({
    descripcion: '',
    categoria: 'Adulto',
    precio_costo: '',
    precio: '',
    proveedor: '',
    images: [],
    oferta: false,
    precio_oferta: '',
    descuento: '',
    novedad: false,
  });
  const [tallas, setTallas] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sync precio_oferta when base precio changes
  useEffect(() => {
    if (formData.descuento && parseInt(formData.descuento) > 0) {
      handleDescuentoChange(formData.descuento);
    }
  }, [formData.precio]);

  useEffect(() => {
    if (product) {
      setFormData({
        descripcion: product.descripcion || '',
        categoria: product.categoria || 'Adulto',
        precio_costo: product.precio_costo || '',
        precio: product.precio || '',
        proveedor: product.proveedor || '',
        images: product.images || [],
        oferta: product.oferta || false,
        precio_oferta: product.precio_oferta || '',
        descuento: product.descuento || '',
        novedad: product.novedad || false,
      });
      setTallas(product.tallas || {});
    }
  }, [product]);

  const availableSizes = SIZE_OPTIONS[formData.categoria] || SIZE_OPTIONS.Adulto;

  const toggleSize = (size) => {
    setTallas(prev => {
      const copy = { ...prev };
      if (copy[size] !== undefined) {
        delete copy[size];
      } else {
        copy[size] = 1;
      }
      return copy;
    });
  };

  const updateSizeStock = (size, stock) => {
    setTallas(prev => ({ ...prev, [size]: Math.max(0, parseInt(stock) || 0) }));
  };

  const handleDescuentoChange = (value) => {
    const desc = parseInt(value) || 0;
    const precioBase = parseFloat(formData.precio) || 0;
    let nuevoPrecioOferta = '';
    
    if (precioBase > 0 && desc > 0) {
      nuevoPrecioOferta = (precioBase * (1 - desc / 100)).toFixed(2);
    }
    
    setFormData(prev => ({ 
      ...prev, 
      descuento: value,
      precio_oferta: nuevoPrecioOferta 
    }));
  };

  const handlePrecioOfertaChange = (value) => {
    const pOferta = parseFloat(value) || 0;
    const precioBase = parseFloat(formData.precio) || 0;
    let nuevoDescuento = '';
    
    if (precioBase > 0 && pOferta > 0) {
      nuevoDescuento = Math.round(((precioBase - pOferta) / precioBase) * 100);
    }
    
    setFormData(prev => ({ 
      ...prev, 
      precio_oferta: value,
      descuento: nuevoDescuento.toString()
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (Object.keys(tallas).length === 0) {
      toast.error('Selecciona al menos una talla');
      return;
    }

    setIsSubmitting(true);
    try {
      const body = {
        descripcion: formData.descripcion.toUpperCase().trim(),
        categoria: formData.categoria,
        tallas: tallas,
        precio: parseFloat(formData.precio),
        precio_costo: parseFloat(formData.precio_costo),
        proveedor: formData.proveedor || 'Sin especificar',
        images: formData.images,
        oferta: formData.oferta,
        precio_oferta: (formData.oferta || (formData.descuento && parseInt(formData.descuento) > 0)) && formData.precio_oferta ? parseFloat(formData.precio_oferta) : null,
        descuento: formData.descuento && parseInt(formData.descuento) > 0 ? parseInt(formData.descuento) : null,
        novedad: formData.novedad,
      };

      const token = sessionStorage.getItem('inversionesfu_token');
      const res = await fetch(`${BACKEND_URL}/api/products/${product.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error((await res.json()).detail || 'Error');

      toast.success('Producto actualizado exitosamente');
      onProductUpdated();
      onClose();
    } catch (err) {
      toast.error('Error al actualizar', { description: err.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!product) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#1A1A1A] border-border max-w-xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-xl font-bold text-foreground flex items-center justify-between">
            Editar Producto
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Images */}
            <ImageUploader images={formData.images} onChange={(imgs) => setFormData({ ...formData, images: imgs })} maxImages={100} />

            {/* Description (editable) */}
            <div className="space-y-2">
              <Label htmlFor="edit-descripcion">Nombre del Producto <span className="text-destructive">*</span></Label>
              <Input id="edit-descripcion" value={formData.descripcion} onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                className="bg-[#121212] border-border text-foreground uppercase" required data-testid="edit-product-name" />
            </div>

            {/* Bloque Novedad - Posición Destacada Arriba */}
            <div className="bg-[#0a0a1a] border border-[#0070FF]/40 rounded-xl p-3 shadow-[0_0_15px_rgba(0,112,255,0.1)] mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#0070FF]/10 rounded-lg">
                    <Star className="w-4 h-4 text-[#0070FF]" />
                  </div>
                  <div>
                    <Label className="text-[#0070FF] font-black text-xs uppercase tracking-widest leading-none">DESTACAR EN NOVEDADES</Label>
                    <p className="text-[9px] text-gray-500 italic mt-1">Aparecerá en la sección de Novedades del Home</p>
                  </div>
                </div>
                <button type="button" onClick={() => setFormData({ ...formData, novedad: !formData.novedad })}
                  className={`relative w-12 h-6 rounded-full transition-all ${formData.novedad ? 'bg-[#0070FF] shadow-[0_0_10px_#0070FF]' : 'bg-gray-800'}`}>
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${formData.novedad ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label>Categoría</Label>
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none snap-x active:cursor-grabbing">
              {Object.keys(SIZE_OPTIONS).map(cat => (
                <button key={cat} type="button" onClick={() => setFormData({ ...formData, categoria: cat })}
                  className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all whitespace-nowrap snap-start flex-shrink-0 ${
                    formData.categoria === cat 
                      ? 'bg-[#39FF14] text-black shadow-[0_0_15px_rgba(57,255,20,0.4)] scale-105' 
                      : 'bg-[#121212] text-gray-400 border border-gray-700 hover:border-gray-600'
                  }`}>
                  {cat}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground italic">← Desliza para ver más categorías →</p>
            </div>

            {/* Sizes with stock */}
            <div className="space-y-2">
              <Label>Tallas y Stock <span className="text-destructive">*</span></Label>
              <div className="grid grid-cols-4 gap-2">
                {availableSizes.map(size => {
                  const isActive = tallas[size] !== undefined;
                  return (
                    <div key={size} className="space-y-1">
                      <button type="button" onClick={() => toggleSize(size)}
                        className={`w-full py-2 rounded-lg text-sm font-bold transition-all ${isActive ? 'bg-[#39FF14] text-black' : 'bg-[#121212] text-gray-500 border border-gray-700 hover:border-gray-500'}`}>
                        {size}
                      </button>
                      {isActive && (
                        <Input type="number" min="0" value={tallas[size]} onChange={(e) => updateSizeStock(size, e.target.value)}
                          className="bg-[#121212] border-border text-foreground text-center text-sm h-8" />
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Show any extra sizes that exist in tallas but not in current category */}
              {Object.keys(tallas).filter(s => !availableSizes.includes(s)).length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-muted-foreground mb-1">Tallas adicionales:</p>
                  <div className="grid grid-cols-4 gap-2">
                    {Object.keys(tallas).filter(s => !availableSizes.includes(s)).map(size => (
                      <div key={size} className="space-y-1">
                        <button type="button" onClick={() => toggleSize(size)}
                          className="w-full py-2 rounded-lg text-sm font-bold bg-[#39FF14] text-black">{size}</button>
                        <Input type="number" min="0" value={tallas[size]} onChange={(e) => updateSizeStock(size, e.target.value)}
                          className="bg-[#121212] border-border text-foreground text-center text-sm h-8" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Secciones Promocionales Independientes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              {/* Bloque Oferta */}
              <div className="space-y-3 bg-[#1a0a0a] border border-[#FF003C]/30 rounded-xl p-3">
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <Label className="text-[#FF003C] font-black text-[10px] uppercase tracking-widest">OFERTA</Label>
                    <p className="text-[8px] text-gray-500 italic">Sección Roja</p>
                  </div>
                  <button type="button" onClick={() => setFormData({ ...formData, oferta: !formData.oferta })}
                    className={`relative w-10 h-5 rounded-full transition-all ${formData.oferta ? 'bg-[#FF003C]' : 'bg-gray-800'}`}>
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${formData.oferta ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </div>
                {formData.oferta && (
                  <div className="space-y-2">
                    <Label className="text-[9px] text-gray-400 uppercase">Precio Oferta (USD)</Label>
                    <Input type="number" step="0.01" min="0" value={formData.precio_oferta}
                      onChange={(e) => handlePrecioOfertaChange(e.target.value)}
                      className="bg-[#121212] border-[#FF003C]/30 text-[#FF003C] font-bold h-8 text-xs" placeholder="USD $" />
                  </div>
                )}
              </div>

              {/* Bloque Descuento % */}
              <div className="space-y-3 bg-[#0a1a0a] border border-[#39FF14]/30 rounded-xl p-3">
                <div>
                  <Label className="text-[#39FF14] font-black text-[10px] uppercase tracking-widest">DESCUENTO %</Label>
                  <p className="text-[8px] text-gray-500 italic">Sección Verde</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-[9px] text-gray-400 uppercase">% Desc</Label>
                    <Input type="number" min="0" max="100" value={formData.descuento}
                      onChange={(e) => handleDescuentoChange(e.target.value)}
                      className="bg-[#121212] border-[#39FF14]/30 text-[#39FF14] font-bold h-8 text-xs" placeholder="%" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[9px] text-gray-400 uppercase">Result</Label>
                    <Input type="number" step="0.01" value={formData.precio_oferta}
                      onChange={(e) => handlePrecioOfertaChange(e.target.value)}
                      className="bg-[#121212] border-[#39FF14]/30 text-[#39FF14] font-bold h-8 text-xs" placeholder="Result" />
                  </div>
                </div>
              </div>

            </div>

            {/* Prices */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Costo (USD) <span className="text-destructive">*</span></Label>
                <Input type="number" step="0.01" min="0" value={formData.precio_costo}
                  onChange={(e) => setFormData({ ...formData, precio_costo: e.target.value })}
                  className="bg-[#121212] border-border text-foreground" required />
              </div>
              <div className="space-y-2">
                <Label>Precio Venta (USD) <span className="text-destructive">*</span></Label>
                <Input type="number" step="0.01" min="0" value={formData.precio}
                  onChange={(e) => setFormData({ ...formData, precio: e.target.value })}
                  className="bg-[#121212] border-border text-foreground" required />
              </div>
            </div>

            {/* Provider */}
            <div className="space-y-2">
              <Label>Proveedor</Label>
              <Input value={formData.proveedor} onChange={(e) => setFormData({ ...formData, proveedor: e.target.value })}
                className="bg-[#121212] border-border text-foreground" />
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-4 border-t border-border">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancelar</Button>
              <Button type="submit" disabled={isSubmitting} className="flex-1 bg-[#39FF14] hover:bg-[#39FF14]/90 text-black font-bold"
                style={{ boxShadow: '0 0 10px #39FF14' }}>
                {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};
